// Dates and numbers as the screen shows them.
//
// Promise:
// - Every date is shown in the given time zone (the account's, or the browser's
//   when unset; `useFormat` picks it). Days ("today", "tomorrow") are counted in
//   that zone too, so due dates and the secretary's jobs agree.
// - Japanese is built by hand (`8/29 0:52`, `2025/8/29`), other languages use Intl.
// - Within this year a date shows month, day and time; other years show the date
//   only. Seconds only in the full form (for a tooltip).
import { getLocale, m } from "@trameria/i18n";

export interface ZoneOptions {
  /** IANA time zone. Undefined = the browser's. */
  timeZone?: string;
}

interface Parts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

const partsFormatters = new Map<string, Intl.DateTimeFormat>();

/** The wall-clock parts of `d` in `timeZone`. */
export function zonedParts(d: Date, timeZone?: string): Parts {
  const key = timeZone ?? "";
  let f = partsFormatters.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      hourCycle: "h23",
    });
    partsFormatters.set(key, f);
  }
  const out: Record<string, number> = {};
  for (const p of f.formatToParts(d))
    if (p.type !== "literal") out[p.type] = Number(p.value);
  return out as unknown as Parts;
}

const pad2 = (n: number) => String(n).padStart(2, "0");
const isJa = () => getLocale() === "ja";
const intl = (opts: Intl.DateTimeFormatOptions, d: Date, timeZone?: string) =>
  new Intl.DateTimeFormat(getLocale(), { ...opts, timeZone }).format(d);
const DATE: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "numeric",
};

export function formatDateTime(
  iso: string,
  { timeZone }: ZoneOptions = {},
  now: Date = new Date(),
): string {
  const d = new Date(iso);
  const p = zonedParts(d, timeZone);
  const thisYear = p.year === zonedParts(now, timeZone).year;
  if (!isJa())
    return thisYear
      ? intl(
          {
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
          },
          d,
          timeZone,
        )
      : intl(DATE, d, timeZone);
  return thisYear
    ? `${p.month}/${p.day} ${p.hour}:${pad2(p.minute)}`
    : `${p.year}/${p.month}/${p.day}`;
}

export function formatTime(iso: string, { timeZone }: ZoneOptions = {}) {
  const d = new Date(iso);
  if (!isJa()) return intl({ hour: "numeric", minute: "2-digit" }, d, timeZone);
  const p = zonedParts(d, timeZone);
  return `${p.hour}:${pad2(p.minute)}`;
}

export function formatDay(iso: string, { timeZone }: ZoneOptions = {}) {
  const d = new Date(iso);
  if (!isJa()) return intl(DATE, d, timeZone);
  const p = zonedParts(d, timeZone);
  return `${p.year}/${p.month}/${p.day}`;
}

/** The day written out in the locale's words ("November 1, 2026"): a day line in a conversation. */
export function formatDayLong(iso: string, { timeZone }: ZoneOptions = {}) {
  return intl(
    { year: "numeric", month: "long", day: "numeric" },
    new Date(iso),
    timeZone,
  );
}

export function formatDateTimeFull(
  iso: string,
  { timeZone }: ZoneOptions = {},
) {
  const p = zonedParts(new Date(iso), timeZone);
  return (
    `${p.year}/${pad2(p.month)}/${pad2(p.day)} ` +
    `${pad2(p.hour)}:${pad2(p.minute)}:${pad2(p.second)}`
  );
}

/** "3 minutes ago"; under a minute (and the future) is "just now". */
export function formatRelative(iso: string, now: Date = new Date()): string {
  const diff = now.getTime() - new Date(iso).getTime();
  if (Number.isNaN(diff) || diff < 60_000) return m.common_just_now();
  const min = Math.floor(diff / 60_000);
  const hour = Math.floor(min / 60);
  const day = Math.floor(hour / 24);
  if (isJa()) {
    if (min < 60) return m.common_minutes_ago({ n: min });
    if (hour < 24) return m.common_hours_ago({ n: hour });
    return m.common_days_ago({ n: day });
  }
  const rtf = new Intl.RelativeTimeFormat(getLocale(), { numeric: "always" });
  if (min < 60) return rtf.format(-min, "minute");
  if (hour < 24) return rtf.format(-hour, "hour");
  return rtf.format(-day, "day");
}

/** Days from today to `iso`, counted in the time zone. */
export function dayDistance(
  iso: string,
  { timeZone }: ZoneOptions = {},
  now: Date = new Date(),
): number {
  const day = (d: Date) => {
    const p = zonedParts(d, timeZone);
    return Date.UTC(p.year, p.month - 1, p.day) / 86_400_000;
  };
  return day(new Date(iso)) - day(now);
}

/** "today", "tomorrow", "in 3 days", counted in the time zone. */
export function formatRelativeDay(
  iso: string,
  zone: ZoneOptions = {},
  now: Date = new Date(),
): string {
  if (Number.isNaN(new Date(iso).getTime())) return "";
  const days = dayDistance(iso, zone, now);
  if (!isJa())
    return new Intl.RelativeTimeFormat(getLocale(), { numeric: "auto" }).format(
      days,
      "day",
    );
  if (days === 0) return m.common_today();
  if (days === 1) return m.common_tomorrow();
  if (days === -1) return m.common_yesterday();
  return days > 0
    ? m.common_days_later({ n: days })
    : m.common_days_ago({ n: -days });
}

export function formatNumber(n: number): string {
  return new Intl.NumberFormat(getLocale()).format(n);
}

export function joinList(items: readonly string[]): string {
  return new Intl.ListFormat(getLocale(), { type: "conjunction" }).format(
    items,
  );
}
